import React, { useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import { useT } from "../lib/i18n";

const MAX_DOCS = 3;
const MAX_MB = 5;

export const DocumentUpload = ({ documents, onChange }) => {
  const { t } = useT();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (documents.length + list.length > MAX_DOCS) {
      toast.error(`${t("Maximum")} ${MAX_DOCS} ${t("documents per session")}`);
      return;
    }
    setUploading(true);
    for (const f of list) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name}: ${t("File too large. Maximum size is 5 MB.")}`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/documents/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange((docs) => [...docs, { filename: r.data.filename, text: r.data.text, char_count: r.data.char_count, truncated: r.data.truncated }]);
        if (r.data.truncated) toast.info(`${r.data.filename}: ${t("document truncated to fit the context limit")}`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || t("Could not process document")}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid="document-upload" className="space-y-2">
      <input ref={inputRef} type="file" accept=".pdf,.docx,.txt,.csv" multiple hidden
        data-testid="document-file-input"
        onChange={(e) => handleFiles(e.target.files)} />
      {documents.map((d, i) => (
        <div key={`${d.filename}-${i}`} data-testid={`document-item-${i}`} className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <FileText className="w-4 h-4 text-[#0EA5E9] shrink-0" />
          <span className="font-bold text-[#0B1B3D] truncate">{d.filename}</span>
          <span className="font-mono-kz text-xs text-[#94A3B8] shrink-0">{(d.char_count / 1000).toFixed(1)}k {t("chars")}</span>
          <button data-testid={`document-remove-${i}`} onClick={() => onChange((docs) => docs.filter((_, j) => j !== i))}
            className="ml-auto text-[#94A3B8] hover:text-[#EF4444] shrink-0"><X className="w-4 h-4" /></button>
        </div>
      ))}
      {documents.length < MAX_DOCS && (
        <button data-testid="document-add-btn" type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-[#0EA5E9] rounded-lg px-3 py-3 text-sm font-bold text-[#475569] hover:text-[#0B1B3D] transition-colors disabled:opacity-50">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          {uploading ? t("Extracting text…") : t("Attach documents (PDF, DOCX, TXT, CSV)")}
        </button>
      )}
      <p className="text-xs text-[#94A3B8] font-mono-kz">{t("Up to 3 files, 5 MB each. Experts will use them as business context (+5 credits).")}</p>
    </div>
  );
};
